/**
 * The value model shared by the YAML and XML converters.
 *
 * Objects are `Map`s, not plain objects: a Map keeps keys in the order they were written
 * (a plain object moves keys such as "2" to the front), accepts a key called
 * `__proto__`, and cannot be tricked into prototype pollution by hostile input.
 * `RawNumber` carries a JSON number exactly as written, so 10.0 stays 10.0.
 */

export class RawNumber {
  readonly text: string;
  constructor(text: string) {
    this.text = text;
  }
}

export type DataMap = Map<string, DataValue>;

export type DataValue =
  | null
  | boolean
  | number
  | bigint
  | string
  | RawNumber
  | DataValue[]
  | DataMap;

/** A problem with a stable code, so the widget can word it in the reader's language. */
export class DataError extends Error {
  readonly code: string;
  readonly params: Readonly<Record<string, string | number>>;
  readonly line: number;
  readonly column: number;

  constructor(
    code: string,
    line: number,
    column: number,
    params: Record<string, string | number> = {}
  ) {
    super(code);
    this.code = code;
    this.line = line;
    this.column = column;
    this.params = params;
  }
}

export interface DataWarning {
  code: string;
  line: number;
  params: Readonly<Record<string, string | number>>;
}

export function isMap(value: DataValue): value is DataMap {
  return value instanceof Map;
}

export type JsonIndent = "2" | "4" | "tab" | "compact";

const INDENT_TEXT: Readonly<Record<JsonIndent, string>> = {
  "2": "  ",
  "4": "    ",
  tab: "\t",
  compact: "",
};

interface Writer {
  out: string[];
  size: number;
  limit: number;
  unit: string;
}

function push(w: Writer, text: string): void {
  w.size += text.length;
  if (w.size > w.limit) {
    throw new DataError("tooLarge", 0, 0, { limit: w.limit });
  }
  w.out.push(text);
}

function newline(w: Writer, depth: number): string {
  return w.unit === "" ? "" : `\n${w.unit.repeat(depth)}`;
}

function writeScalar(w: Writer, node: DataValue): boolean {
  if (node === null) {
    push(w, "null");
  } else if (typeof node === "string") {
    push(w, JSON.stringify(node));
  } else if (typeof node === "number") {
    push(w, Number.isFinite(node) ? String(node) : "null");
  } else if (typeof node === "bigint") {
    push(w, node.toString());
  } else if (typeof node === "boolean") {
    push(w, node ? "true" : "false");
  } else if (node instanceof RawNumber) {
    push(w, node.text);
  } else {
    return false;
  }
  return true;
}

function writeList(w: Writer, list: DataValue[], depth: number): void {
  if (list.length === 0) {
    push(w, "[]");
    return;
  }
  push(w, "[");
  list.forEach((item, i) => {
    push(w, `${i > 0 ? "," : ""}${newline(w, depth + 1)}`);
    writeValue(w, item, depth + 1);
  });
  push(w, `${newline(w, depth)}]`);
}

function writeObject(w: Writer, map: DataMap, depth: number): void {
  if (map.size === 0) {
    push(w, "{}");
    return;
  }
  push(w, "{");
  let first = true;
  for (const [key, item] of map) {
    push(w, `${first ? "" : ","}${newline(w, depth + 1)}`);
    push(w, `${JSON.stringify(key)}:${w.unit === "" ? "" : " "}`);
    writeValue(w, item, depth + 1);
    first = false;
  }
  push(w, `${newline(w, depth)}}`);
}

function writeValue(w: Writer, node: DataValue, depth: number): void {
  if (writeScalar(w, node)) {
    return;
  }
  if (Array.isArray(node)) {
    writeList(w, node, depth);
  } else {
    writeObject(w, node as DataMap, depth);
  }
}

/** Serialise a value as JSON, keeping key order. Throws when the output passes `limit`. */
export function stringifyJson(
  value: DataValue,
  indent: JsonIndent,
  limit: number
): string {
  const writer: Writer = { out: [], size: 0, limit, unit: INDENT_TEXT[indent] };
  writeValue(writer, value, 0);
  return writer.out.join("");
}
